import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { MainNavigation } from '../components/navigation/MainNavigation'
import { MainFooter } from '../components/navigation/MainFooter'
import { useAuthStore } from '../stores/authStore'
import { NAVIGATION_ACTIONS } from '../config/navigation.config'
import { ServiceConfigUtils } from '../config/services.utils'
import { getIconComponent } from '../config/icons.config'
import { 
  Check,
  CheckCircle,
  X,
  Star,
  Zap,
  Shield,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Clock,
  Users,
  BarChart3,
  Briefcase,
  Building2,
  Rocket,
  Info,
  Award,
  TrendingUp,
  Target,
  DollarSign,
  Calendar,
  HeartHandshake,
  Sparkles,
  Crown,
  Timer,
  FileText,
  Phone,
  MessageSquare,
  Video,
  Mail,
  Calculator
} from 'lucide-react'

interface ServiceTier {
  id: string
  name: string
  description: string
  price: string
  deposit: string
  billingType: string
  features: string[]
  icon: React.ReactNode
  headerColor: string
  popular?: boolean
  comingSoon?: boolean
  tierLevel?: 'quick' | 'standard' | 'comprehensive' | 'enterprise'
  metadata: {
    deliveryTime?: string
    reportPages?: string
    targetRevenue?: string
  }
}

const getTierBadgeConfig = (tierLevel?: string) => {
  switch (tierLevel) {
    case 'quick':
      return {
        label: 'QUICK',
        bgClass: 'bg-gradient-to-r from-emerald-500 to-green-500',
        icon: <Zap className="h-4 w-4" />
      }
    case 'standard':
      return {
        label: 'STANDARD',
        bgClass: 'bg-gradient-to-r from-blue-500 to-cyan-500',
        icon: <BarChart3 className="h-4 w-4" />
      }
    case 'comprehensive':
      return {
        label: 'COMPREHENSIVE',
        bgClass: 'bg-gradient-to-r from-violet-500 to-purple-500',
        icon: <Target className="h-4 w-4" />
      }
    case 'enterprise':
      return {
        label: 'ENTERPRISE',
        bgClass: 'bg-gradient-to-r from-amber-500 to-orange-500',
        icon: <Award className="h-4 w-4" />
      }
    default:
      return null
  }
}

const formatPriceWithSmallDecimals = (priceDisplay: string) => {
  // Split price like "$49.00 USD" into parts
  const match = priceDisplay.match(/^(\$\d+)(\.00)(\s+\w+)$/)
  if (!match) return priceDisplay // Return as-is if format doesn't match

  const [, dollars, cents, currency] = match
  return { dollars, cents, currency }
}

export const PricingPage: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null)
  const [selectedService, setSelectedService] = useState<string | null>(null)

  const handleGetStarted = (serviceId?: string) => {
    if (serviceId) {
      setSelectedService(serviceId)
      // Store selected service in localStorage for onboarding flow
      localStorage.setItem('selected_service_id', serviceId)
    }
    navigate(NAVIGATION_ACTIONS.primary.path)
  }

  // Load services dynamically from configuration
  const activeServices = ServiceConfigUtils.getActiveServices()
  const comingSoonServices = ServiceConfigUtils.getComingSoonServices()
  
  // Transform services to display format (only show active services)
  const serviceTiers: ServiceTier[] = activeServices.map(service => {
    const IconComponent = getIconComponent(service.icon)
    
    // Fallback to BarChart3 if icon not found
    const FallbackIcon = IconComponent || BarChart3
    
    return {
      id: service.id,
      name: service.name,
      description: service.description,
      price: service.price.display,
      deposit: service.deposit.display,
      billingType: service.billingType,
      features: service.features,
      icon: <FallbackIcon className="h-6 w-6" />,
      headerColor: service.headerColor,
      popular: service.popular,
      comingSoon: service.comingSoon,
      tierLevel: service.tierLevel,
      metadata: service.metadata || {}
    }
  })


  const faqs = [
    {
      question: "How does StratIQX compare to hiring consultants?",
      answer: "StratIQX delivers Big 3 consulting quality analysis at 5% of the cost and 100x faster. While traditional consultants charge $300K-$500K for a 3-6 month engagement, StratIQX provides unlimited analyses for a fraction of the price with 4-hour delivery."
    },
    {
      question: "What exactly is included in each strategic analysis?",
      answer: "Each analysis includes: comprehensive market research, competitive intelligence, financial modeling, growth opportunity identification, risk assessment, strategic recommendations with implementation roadmaps, and executive summaries. Everything is tailored to your specific business context and decision needs."
    },
    {
      question: "Can I change my plan later?",
      answer: "Absolutely! You can upgrade or downgrade your plan at any time. Upgrades take effect immediately with prorated billing, while downgrades take effect at the next billing cycle. No long-term contracts required."
    },
    {
      question: "What's the difference between analyses in each tier?",
      answer: "The core analysis quality is consistent across all tiers - you get the same AI-powered intelligence engine. The differences are in delivery speed, number of analyses, additional features (like real-time monitoring), and support level. Enterprise tier also includes customization and white-labeling options."
    },
    {
      question: "How do you ensure analysis quality?",
      answer: "Every analysis combines our proprietary AI engine with expert validation. We use multiple data sources, cross-reference findings, and apply industry-specific frameworks. All deliverables undergo automated quality checks and validation."
    },
    {
      question: "What if I need more analyses than my plan includes?",
      answer: "Starter and Growth plans can purchase additional analyses at $3,500 each. However, if you consistently need more, upgrading your plan is more cost-effective. Enterprise plans include unlimited analyses."
    },
    {
      question: "Is there a setup fee or onboarding cost?",
      answer: "No setup fees! We include complimentary onboarding for all plans: Starter gets email onboarding, Growth gets a 1-hour video session, and Enterprise gets comprehensive multi-session training for your entire team."
    }
  ]

  const comparisonData = [
    { feature: 'Report Length', executiveBrief: '3-5 pages', quick: '8-12 pages', standard: '20-25 pages', comprehensive: '25-35 pages', enterprise: '40+ pages' },
    { feature: 'Delivery Time', executiveBrief: '4 hours', quick: '4 hours', standard: '4 hours', comprehensive: '4 hours', enterprise: '4 hours' },
    { feature: 'Analysis Sections', executiveBrief: '2 sections', quick: '4 sections', standard: '5 sections', comprehensive: '6 sections', enterprise: '8 sections' },
    { feature: 'AI Processing Depth', executiveBrief: '2,000 tokens/section', quick: '1,200 tokens/section', standard: '2,200 tokens/section', comprehensive: '2,048 tokens/section', enterprise: '2,048 tokens/section' },
    { feature: 'Export Format', executiveBrief: 'PDF only', quick: 'PDF only', standard: 'PDF only', comprehensive: 'PDF only', enterprise: 'PDF only' },
    { feature: 'Document Quality', executiveBrief: 'LaTeX + Chicago', quick: 'LaTeX + Chicago', standard: 'LaTeX + Chicago', comprehensive: 'LaTeX + Chicago', enterprise: 'LaTeX + Chicago' },
    { feature: 'Consulting Value', executiveBrief: '$100,000+', quick: '$10,000', standard: '$25,000', comprehensive: '$50,000', enterprise: '$75,000+' },
    { feature: 'Quality Score', executiveBrief: '98/100', quick: '75/100', standard: '85/100', comprehensive: '92/100', enterprise: '95/100' },
    { feature: 'Best For', executiveBrief: 'Board decisions', quick: 'Quick assessments', standard: 'Quarterly planning', comprehensive: 'Annual strategy', enterprise: 'Enterprise transformation' },
    { feature: 'Processing Model', executiveBrief: 'Scout 17B', quick: 'Scout 17B', standard: 'Scout 17B', comprehensive: 'Scout 17B', enterprise: 'Scout 17B' }
  ]

  const testimonials = [
    {
      quote: "StratIQX replaced our $400K annual consulting spend. We now get better insights, faster, at 5% of the cost.",
      author: "Michael Chen",
      title: "CEO, TechVentures Inc.",
      tier: "Enterprise"
    },
    {
      quote: "The ROI was immediate. Our first analysis identified a $2M opportunity we were completely blind to.",
      author: "Sarah Williams",
      title: "COO, Growth Dynamics",
      tier: "Growth"
    },
    {
      quote: "As a small business, we finally have access to enterprise-grade strategic intelligence we can afford.",
      author: "David Martinez",
      title: "Founder, Martinez Industries",
      tier: "Starter"
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <MainNavigation variant="full" />

      {/* Hero Section */}
      <section className="relative py-12 px-4 text-center">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-transparent" />
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="mb-6 inline-flex items-center bg-blue-600/20 px-4 py-2 rounded-full border border-blue-500/30">
            <DollarSign className="h-5 w-5 text-cyan-400 mr-2" />
            <span className="text-cyan-400 font-semibold">Transparent, Value-Based Pricing</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 bg-gradient-to-r from-white to-cyan-300 bg-clip-text text-transparent">
            Strategic Intelligence Services
          </h1>
          
          <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto mb-8">
            Professional strategic intelligence services. Choose the analysis that fits your business needs.
          </p>

          {/* Quick Stats */}
          <div className="flex flex-wrap justify-center gap-4">
            <div className="bg-slate-800/50 px-6 py-3 rounded-lg border border-blue-700/30">
              <div className="text-3xl font-bold text-cyan-400">5%</div>
              <div className="text-sm text-gray-400">of Consulting Cost</div>
            </div>
            <div className="bg-slate-800/50 px-6 py-3 rounded-lg border border-blue-700/30">
              <div className="text-3xl font-bold text-cyan-400">100x</div>
              <div className="text-sm text-gray-400">Faster Delivery</div>
            </div>
            <div className="bg-slate-800/50 px-6 py-3 rounded-lg border border-blue-700/30">
              <div className="text-3xl font-bold text-cyan-400">327%</div>
              <div className="text-sm text-gray-400">Average ROI</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {serviceTiers.map((service) => (
              <Card 
                key={service.id}
                className={`bg-slate-800/80 border-blue-800/30 backdrop-blur-sm relative overflow-hidden transform transition-all duration-300 hover:scale-105 ${
                  service.popular ? 'ring-2 ring-cyan-500' : ''
                } ${service.comingSoon ? 'opacity-75' : ''}`}
              >
                {service.popular && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                    <Star className="h-4 w-4 inline mr-1" />
                    MOST POPULAR
                  </div>
                )}
                
                {service.comingSoon && (
                  <div className="absolute top-0 left-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-1 text-sm font-semibold rounded-br-lg">
                    <Clock className="h-4 w-4 inline mr-1" />
                    COMING SOON
                  </div>
                )}

                {/* Tier Badge */}
                {service.tierLevel && (() => {
                  const tierConfig = getTierBadgeConfig(service.tierLevel)
                  if (!tierConfig) return null
                  
                  return (
                    <div className={`absolute ${service.popular ? 'top-8 right-0' : 'top-0 right-0'} ${tierConfig.bgClass} text-white px-3 py-1 text-xs font-semibold rounded-bl-lg flex items-center gap-1 z-10`}>
                      {tierConfig.icon}
                      {tierConfig.label}
                    </div>
                  )
                })()}
                
                <div className={`bg-gradient-to-r ${service.headerColor} px-6 py-6`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="bg-white/20 w-12 h-12 rounded-full flex items-center justify-center text-white">
                      {service.icon}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-white">{service.name}</h3>
                  <p className="text-white/80 text-sm">{service.billingType === 'one-time' ? 'One-time Service' : 'Monthly Service'}</p>
                </div>

                <CardContent className="p-6">
                  <div className="mb-6">
                    <div className="flex items-baseline">
                      {(() => {
                        const priceFormatted = formatPriceWithSmallDecimals(service.price)
                        if (typeof priceFormatted === 'string') {
                          return <span className="text-4xl font-bold text-white">{priceFormatted}</span>
                        }
                        return (
                          <span className="font-bold text-white">
                            <span className="text-4xl">{priceFormatted.dollars}</span>
                            <span className="text-2xl">{priceFormatted.cents}</span>
                            <span className="text-2xl ml-1">{priceFormatted.currency}</span>
                          </span>
                        )
                      })()}
                      {service.billingType === 'one-time' && (
                        <span className="text-gray-400 ml-2">one-time</span>
                      )}
                    </div>
                    {service.deposit !== 'No upfront payment required' && service.deposit !== 'No credit card required' && (
                      <p className="text-sm text-amber-400 mt-1">
                        {service.deposit}
                      </p>
                    )}
                  </div>

                  <p className="text-gray-300 mb-6">{service.description}</p>

                  {/* Key Metrics */}
                  {service.metadata.deliveryTime && (
                    <div className="bg-slate-700/30 p-3 rounded-lg mb-6">
                      <div className="text-2xl font-bold text-green-400">{service.metadata.deliveryTime}</div>
                      <div className="text-xs text-gray-400">Delivery Time</div>
                    </div>
                  )}

                  {/* Features */}
                  <div className="space-y-3 mb-6">
                    {service.features.map((feature, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Target Revenue */}
                  {service.metadata.targetRevenue && (
                    <div className="border-t border-slate-700 pt-4 mb-6">
                      <p className="text-xs text-gray-400 mb-2">IDEAL FOR:</p>
                      <p className="text-sm text-gray-300">• {service.metadata.targetRevenue} businesses</p>
                      {service.metadata.reportPages && (
                        <p className="text-sm text-gray-300">• {service.metadata.reportPages} page report</p>
                      )}
                    </div>
                  )}

                  <Button
                    onClick={() => handleGetStarted(service.id)}
                    disabled={service.comingSoon}
                    className={`w-full ${
                      service.popular 
                        ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500' 
                        : 'bg-slate-700 hover:bg-slate-600'
                    } text-white disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {service.comingSoon ? 'Coming Soon' : 'Get Started'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* AI Intelligence Service Comparison */}
      <section className="py-16 px-4 bg-slate-900/50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            AI Intelligence Service Comparison
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-3xl mx-auto">
            Compare our AI orchestrator detail levels and see exactly what you get with each service tier
          </p>
          
          <Card className="bg-slate-800/80 border-blue-800/30 backdrop-blur-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-600 to-cyan-600">
                    <th className="px-6 py-4 text-left text-white font-semibold">Feature</th>
                    <th className="px-6 py-4 text-center text-white font-semibold">
                      <Crown className="h-5 w-5 inline mr-2" />
                      Brief
                    </th>
                    <th className="px-6 py-4 text-center text-white font-semibold">
                      <Zap className="h-5 w-5 inline mr-2" />
                      Quick
                    </th>
                    <th className="px-6 py-4 text-center text-white font-semibold">
                      <BarChart3 className="h-5 w-5 inline mr-2" />
                      Standard
                    </th>
                    <th className="px-6 py-4 text-center text-white font-semibold">
                      <Target className="h-5 w-5 inline mr-2" />
                      Comprehensive
                    </th>
                    <th className="px-6 py-4 text-center text-white font-semibold">
                      <Award className="h-5 w-5 inline mr-2" />
                      Enterprise
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-slate-800/50' : 'bg-slate-700/30'}>
                      <td className="px-6 py-4 text-gray-300 font-medium">{row.feature}</td>
                      <td className="px-6 py-4 text-center text-gray-300">{row.executiveBrief}</td>
                      <td className="px-6 py-4 text-center text-gray-300">{row.quick}</td>
                      <td className="px-6 py-4 text-center text-gray-300">{row.standard}</td>
                      <td className="px-6 py-4 text-center text-gray-300">{row.comprehensive}</td>
                      <td className="px-6 py-4 text-center text-gray-300">{row.enterprise}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            What Our Clients Say
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-slate-800/80 border-blue-800/30 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-300 italic mb-4">"{testimonial.quote}"</p>
                  <div className="border-t border-slate-700 pt-4">
                    <p className="text-white font-semibold">{testimonial.author}</p>
                    <p className="text-sm text-gray-400">{testimonial.title}</p>
                    <span className="inline-flex items-center bg-blue-600/20 px-2 py-1 rounded-full border border-blue-500/30 mt-2">
                      <span className="text-xs text-cyan-400">{testimonial.tier} Customer</span>
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-4 bg-slate-900/50">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index} className="bg-slate-800/80 border-blue-800/30 backdrop-blur-sm">
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                >
                  <h3 className="text-lg font-semibold text-white">{faq.question}</h3>
                  {expandedFAQ === index ? (
                    <ChevronUp className="h-5 w-5 text-cyan-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {expandedFAQ === index && (
                  <CardContent className="px-6 pb-4">
                    <p className="text-gray-300">{faq.answer}</p>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <Card className="bg-gradient-to-r from-cyan-600 to-blue-600 border-cyan-500/30 overflow-hidden">
            <CardContent className="p-12 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent pointer-events-none" />
              <h2 className="text-4xl font-bold text-white mb-4 relative z-10">
                Ready to Transform Your Strategic Decision-Making?
              </h2>
              <p className="text-xl text-cyan-100 mb-8 relative z-10">
                Join hundreds of companies making smarter decisions with StratIQX
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center relative z-10">
                <Button
                  onClick={() => handleGetStarted()}
                  size="lg"
                  className="bg-white text-blue-600 hover:bg-gray-100"
                >
                  Start Your Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  onClick={() => navigate('/roi-calculator')}
                  size="lg"
                  variant="outline"
                  className="bg-transparent border-2 border-white text-white hover:bg-white/10"
                >
                  Calculate Your ROI
                  <Calculator className="ml-2 h-5 w-5" />
                </Button>
              </div>
              
              <div className="mt-8 flex items-center justify-center space-x-6 text-white/80 relative z-10">
                <div className="flex items-center">
                  <Shield className="h-5 w-5 mr-2" />
                  <span>30-Day Guarantee</span>
                </div>
                <div className="flex items-center">
                  <HeartHandshake className="h-5 w-5 mr-2" />
                  <span>No Setup Fees</span>
                </div>
                <div className="flex items-center">
                  <Timer className="h-5 w-5 mr-2" />
                  <span>Cancel Anytime</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <MainFooter />
    </div>
  )
}